import React from 'react'

import { Button, Popover, PopoverTrigger, getExtendedDialogState, usePopoverState } from '../../components'
import { SpecificChartFunctionality } from '../../constants/common'
import { chartSettingsAsyncModule } from '../../controllers/chart-settings/async-modules'
import ChartLayout from '../../models/chart_layout'
import { useAsyncModule } from '../../utils/async-modules-utils'
import { encodeQueryString } from '../../utils/query_string'

const BUTTON_POPOVER_GUTTER = 16
const SETTINGS_POPOVER_ID = 'settings-popover'

const utmCampaign: Partial<Record<SpecificChartFunctionality, string>> = {
  [SpecificChartFunctionality.quotePage]: 'quote-settings-button',
  [SpecificChartFunctionality.futuresPage]: 'futures-settings-button',
  [SpecificChartFunctionality.forexPage]: 'forex-settings-button',
  [SpecificChartFunctionality.cryptoPage]: 'crypto-settings-button',
}

type PopoverStylesType = { body?: React.CSSProperties; wrapper?: React.CSSProperties }

export function ScrollableChartSettingsPopover({ state }: { state: any }) {
  const [chartsModule] = useAsyncModule({
    ...chartSettingsAsyncModule,
    isSuspenseMode: true,
  })
  const { PreventClosingUnsavedChanges, ChartSettings } = chartsModule
  const { isFullyClosed } = getExtendedDialogState(state)
  const chartElement = document.getElementById('chart')
  const popoverContentElement = document.getElementById(SETTINGS_POPOVER_ID)
  const settingsButton = state.unstable_referenceRef.current

  const popoverStyles = React.useMemo<PopoverStylesType>(() => {
    const newPopoverStyles: PopoverStylesType = {}
    if (!settingsButton || !chartElement) {
      return newPopoverStyles
    }
    const settingsButtonBoundingClientRect = settingsButton.getBoundingClientRect()
    const topOffset = settingsButtonBoundingClientRect.top + settingsButtonBoundingClientRect.height
    newPopoverStyles.body = {
      maxHeight: window.innerHeight - topOffset - BUTTON_POPOVER_GUTTER * 2,
      position: 'sticky',
      top: -topOffset + BUTTON_POPOVER_GUTTER - window.scrollY,
    }
    if (!popoverContentElement) {
      return newPopoverStyles
    }
    const chartHeight = chartElement.clientHeight
    const popoverContentHeight = popoverContentElement.offsetHeight
    newPopoverStyles.wrapper = {
      height: popoverContentHeight < chartHeight ? chartHeight : undefined,
    }

    return newPopoverStyles
  }, [settingsButton, chartElement, popoverContentElement])

  return (
    <PreventClosingUnsavedChanges onHide={state.hide}>
      {({ checkForUnsavedChangesAndClose }) => (
        <Popover
          id={SETTINGS_POPOVER_ID}
          aria-label="Chart Settings"
          className="relative z-10 flex max-h-full w-145 max-w-full flex-col"
          state={state}
          style={popoverStyles.body}
          popoverWrapperStyle={popoverStyles.wrapper}
          focusOnHide={false}
          hideOnClickOutside={false}
          onOverlayClick={checkForUnsavedChangesAndClose}
        >
          {!isFullyClosed && <ChartSettings onSubmit={() => {}} onClose={checkForUnsavedChangesAndClose} />}
        </Popover>
      )}
    </PreventClosingUnsavedChanges>
  )
}

interface ChartSettingsButtonProps {
  isPremium: boolean
  placement?: string
  model: ChartLayout
  theme?: string
  size?: string
  rounding?: string
}

export function ChartSettingsButton({
  isPremium,
  model,
  placement,
  ...props
}: React.PropsWithChildren<ChartSettingsButtonProps>) {
  const [chartsModule] = useAsyncModule({
    ...chartSettingsAsyncModule,
    isSuspenseMode: true,
  })
  const { chartLayoutModel } = chartsModule.useChartLayoutGlobalModel()
  const settingsPopoverState = usePopoverState({ placement: placement ?? 'bottom-start' })

  const buttonProps = React.useMemo(() => {
    if (model.specificChartFunctionality !== SpecificChartFunctionality.quotePage && !isPremium) {
      return {
        as: 'a',
        href: `/elite.ashx?${encodeQueryString({
          utm_source: 'finviz',
          utm_medium: 'banner',
          utm_campaign: utmCampaign[model.specificChartFunctionality],
        })}`,
      }
    }

    return {
      as: PopoverTrigger,
      state: settingsPopoverState,
      active: settingsPopoverState.visible,
    }
  }, [model, isPremium, settingsPopoverState])

  return (
    <>
      <Button
        {...buttonProps}
        focusRing={false}
        data-testid="chart-settings-open-btn"
        disabled={!chartLayoutModel}
        appearance={props.children ? undefined : 'square'}
        title={props.children ? undefined : 'Chart Settings'}
        leftContent="settingsWheel"
        {...props}
      />
      <ScrollableChartSettingsPopover state={settingsPopoverState} />
    </>
  )
}
